import PropTypes from "prop-types";
import * as React from "react";
import fetcher from "../../../hooks/fetcher";
import useInfluencerServices from "../../../hooks/useInfluencerServices";
import { Alert, Box, Button, Dialog } from "../../../themeV2";
import AdvancedDisclosure from "./AdvancedDisclosure";
import {
  buildPayload,
  describeAdvancedSettings,
  NEW_GROUP,
  resolveExclusionGroup,
  validatePayload,
} from "./serviceForm";
import ServiceFormFields from "./ServiceFormFields";
import ServiceTypeChooser from "./ServiceTypeChooser";
import { fieldsFor, hiddenFieldsFor, nounFor } from "./serviceTypes";

export default function ServiceFormDialog({
  influencerId,
  refresh,
  service = null,
  open: controlledOpen,
  onClose: controlledOnClose,
}) {
  const isEditMode = service !== null;
  const isControlled = controlledOpen !== undefined;

  const [internalOpen, setInternalOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  // { field, message } for a validation failure, or { message } for a
  // submission failure that belongs to no single input.
  const [error, setError] = React.useState(null);
  const [requiresText, setRequiresText] = React.useState(
    isEditMode ? service.requiresTextInput : true,
  );
  const [exclusionGroup, setExclusionGroup] = React.useState(
    isEditMode ? service.exclusionGroup ?? "" : "",
  );
  const [chosenCategory, setChosenCategory] = React.useState(null);
  const [advancedRevealed, setAdvancedRevealed] = React.useState(false);

  const isOpen = isControlled ? controlledOpen : internalOpen;
  // Null until the chooser has been answered; a service being edited keeps the
  // category it was created with.
  const category = isEditMode ? service.category : chosenCategory;
  const titleRef = React.useRef(null);

  // Groups already in use by this shop, so a service can join one without
  // anyone having to retype its name exactly.
  const { services: allServices } = useInfluencerServices(influencerId);
  const existingGroups = React.useMemo(
    () =>
      [
        ...new Set(allServices.map((s) => s.exclusionGroup).filter(Boolean)),
      ].sort(),
    [allServices],
  );

  // Select.Root renders its trigger from this value → label map.
  const exclusionGroupItems = React.useMemo(
    () =>
      existingGroups.reduce((items, group) => ({ ...items, [group]: group }), {
        "": "None",
        [NEW_GROUP]: "Create a new group…",
      }),
    [existingGroups],
  );

  /*
   * themeV2 inputs render their own `Field.Error` and set `invalid` when given
   * an `error` string, and Base UI wires the aria-describedby association — so
   * the message belongs on the offending field rather than in a shared block
   * at the foot of the form.
   */
  const errorFor = (field) =>
    error?.field === field ? error.message : undefined;

  const clearFieldError = (field) => () => {
    if (error?.field === field) setError(null);
  };

  const handleClose = () => {
    if (isControlled) {
      controlledOnClose?.();
    } else {
      setInternalOpen(false);
    }
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();

    const formData = Object.fromEntries(new FormData(evt.target));

    const payload = buildPayload({
      category,
      values: {
        ...formData,
        requiresTextInput: requiresText,
        exclusionGroup: resolveExclusionGroup({
          selected: formData.exclusionGroup,
          typed: formData.newExclusionGroup,
        }),
      },
      existing: isEditMode ? service : null,
      advancedRevealed,
    });

    const validationError = validatePayload(payload);
    if (validationError) {
      setError(validationError);
      // Take the admin to the field that needs fixing rather than leaving them
      // to work out which one the message refers to. `validatePayload` names
      // the field by its form control name, so the form's own element
      // collection resolves it.
      const form = evt.target;
      requestAnimationFrame(() =>
        form.elements[validationError.field]?.focus(),
      );
      return;
    }

    setIsLoading(true);
    try {
      const url = isEditMode
        ? `/api/influencer-services/${influencerId}/${service.influencerServiceId}`
        : `/api/influencer-services/${influencerId}`;
      const method = isEditMode ? "PUT" : "POST";

      await fetcher(url, {
        method,
        body: JSON.stringify(payload),
      });
      handleClose();
      if (!isEditMode) {
        setRequiresText(true);
      }
      await refresh();
    } catch (err) {
      setError({
        message:
          err?.json?.message ??
          (isEditMode
            ? "Failed to update service"
            : "Failed to create service"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => {
    setError(null);
    setRequiresText(true);
    setExclusionGroup("");
    setChosenCategory(null);
    setAdvancedRevealed(false);
    setInternalOpen(true);
  };

  const handleBack = () => {
    setError(null);
    setChosenCategory(null);
  };

  React.useEffect(() => {
    if (isOpen && isEditMode) {
      setRequiresText(service.requiresTextInput);
      setExclusionGroup(service.exclusionGroup ?? "");
      setAdvancedRevealed(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, service?.requiresTextInput, service?.exclusionGroup]);

  // The dialog's content is replaced wholesale when the step changes, so focus
  // moves to the new step's heading rather than staying where the old one was.
  React.useEffect(() => {
    if (isOpen) titleRef.current?.focus();
  }, [isOpen, category]);

  const titleId = isEditMode ? "edit-service-title" : "new-service-title";
  const noun = nounFor(category);
  const titleText = isEditMode
    ? `Edit ${noun}`
    : category === null
    ? "What kind of service is this?"
    : `New ${noun}`;
  const submitText = isEditMode ? "Save Changes" : `Create ${noun}`;

  // Only a service already holding settings its own form cannot show has
  // anything to disclose; a new one starts on its type's defaults.
  const advancedSummary = isEditMode ? describeAdvancedSettings(service) : null;

  const fieldProps = {
    category,
    service,
    isLoading,
    errorFor,
    clearFieldError,
    requiresText,
    onRequiresTextChange: setRequiresText,
    exclusionGroup,
    onExclusionGroupChange: setExclusionGroup,
    existingGroups,
    exclusionGroupItems,
  };

  return (
    <>
      {!isControlled && (
        <Button variant="filled" onClick={handleOpen} aria-label="Add service">
          Add Service
        </Button>
      )}

      <Dialog.Root open={isOpen} onOpenChange={handleClose}>
        <Dialog.Popup size="md">
          <Dialog.Close />
          <Dialog.Title id={titleId} tabIndex={-1} ref={titleRef}>
            {titleText}
          </Dialog.Title>

          <Dialog.Body>
            {category === null && (
              <ServiceTypeChooser
                headingId={titleId}
                onChoose={setChosenCategory}
              />
            )}

            {/* No blanket onChange reset — clearing on any keystroke could
                wipe the message before it has been read. Each field clears
                its own. */}
            {category !== null && (
              <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                <Box direction="column" gap={4}>
                  {!isEditMode && (
                    <Box>
                      <Button
                        variant="text"
                        size="sm"
                        disabled={isLoading}
                        onClick={handleBack}
                      >
                        ← Back
                      </Button>
                    </Box>
                  )}

                  <ServiceFormFields
                    {...fieldProps}
                    fields={fieldsFor(category)}
                  />

                  {Boolean(advancedSummary) && (
                    <AdvancedDisclosure
                      summary={advancedSummary}
                      isOpen={advancedRevealed}
                      onToggle={setAdvancedRevealed}
                    >
                      <ServiceFormFields
                        {...fieldProps}
                        fields={hiddenFieldsFor(category)}
                      />
                    </AdvancedDisclosure>
                  )}

                  {/* Validation failures are shown on the field they belong to;
                      this is for submission failures, which belong to none. */}
                  {Boolean(error) && !error.field && (
                    <Alert severity="error">{error.message}</Alert>
                  )}

                  <Button
                    variant="filled"
                    disabled={isLoading}
                    loading={isLoading}
                    type="submit"
                    fullWidth
                  >
                    {submitText}
                  </Button>
                </Box>
              </form>
            )}
          </Dialog.Body>
        </Dialog.Popup>
      </Dialog.Root>
    </>
  );
}

ServiceFormDialog.propTypes = {
  influencerId: PropTypes.number.isRequired,
  refresh: PropTypes.func.isRequired,
  service: PropTypes.shape({
    influencerServiceId: PropTypes.number.isRequired,
    category: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    splitAmount: PropTypes.number.isRequired,
    maxWordCount: PropTypes.number,
    requiresTextInput: PropTypes.bool,
    maxQuantity: PropTypes.number,
    exclusionGroup: PropTypes.string,
  }),
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
